import React, { useState, useEffect } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { fetchStates, fetchDistrictsByState, deleteDistrict } from '../../utils/api';
import MasterDataTable from '../../components/MasterDataTable';

const columns = [
  { id: 'districtId', label: 'ID', minWidth: 50 },
  { id: 'districtName', label: 'District Name', minWidth: 170 },
  { 
    id: 'state', 
    label: 'State', 
    minWidth: 170,
    format: (value) => value?.stateName || '-'
  },
];

const DistrictList = ({ onEdit, searchQuery = '' }) => {
  const [districts, setDistricts] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch states on component mount
  useEffect(() => {
    let cancelled = false;
    const loadStates = async () => {
      try {
        const response = await fetchStates();
        if (cancelled) return;
        setStates(response.data);
        // If there are states, select the first one by default
        if (response.data && response.data.length > 0) {
          setSelectedState(response.data[0].stateId);
        }
      } catch (err) {
        console.error('Error fetching states:', err);
        if (!cancelled) setError('Failed to load states. Please try again later.');
      }
    };
    loadStates();
    return () => {
      cancelled = true;
    };
  }, []);

  // Fetch districts when selected state changes or refresh trigger changes
  useEffect(() => {
    let cancelled = false;
    const loadDistricts = async () => {
      if (!selectedState) return;

      setLoading(true);
      try {
        const response = await fetchDistrictsByState(selectedState);
        if (cancelled) return;
        setDistricts(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching districts:', err);
        if (!cancelled) {
          setError('Failed to load districts. Please try again later.');
          setDistricts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadDistricts();
    return () => {
      cancelled = true;
    };
  }, [selectedState, refreshTrigger]);

  const handleStateChange = (event) => {
    setSelectedState(event.target.value);
  };

  const handleDelete = async (district) => {
    try {
      await deleteDistrict(district.districtId, {
        previousData: district,
        identifier: district.districtName
      });
      setRefreshTrigger(prev => prev + 1);
    } catch (err) {
      console.error('Error deleting district:', err);
      alert('Failed to delete district. It may be referenced by other records.');
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Districts
      </Typography>

      <FormControl fullWidth margin="normal">
        <InputLabel id="state-select-label">Select State</InputLabel>
        <Select
          labelId="state-select-label"
          value={selectedState}
          label="Select State"
          onChange={handleStateChange}
        >
          {states.map((state) => (
            <MenuItem key={state.stateId} value={state.stateId}>
              {state.stateName}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      
      <MasterDataTable
        persistKey="district"
        data={districts}
        columns={columns}
        isLoading={loading}
        searchQuery={searchQuery}
        onEdit={onEdit}
        onDelete={handleDelete}
        emptyMessage={selectedState ? "No districts found for selected state. Add your first district!" : "Please select a state to view districts."}
      />
      
      {error && (
        <Typography color="error" sx={{ mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default DistrictList; 